/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import { isString } from '../../services/predicate';
import { EuiContextMenuItem, EuiContextMenuPanel } from '../context_menu';
import { EuiPopover } from '../popover';
import { EuiButtonIcon } from '../button';
import { EuiToolTip } from '../tool_tip';
import { EuiI18n } from '../i18n';

function actionIsCustomItemAction(action) {
  return action.hasOwnProperty('render');
}

export class CollapsedItemActions extends Component {
  popoverDiv = null;
  state = {
    popoverOpen: false
  };
  togglePopover = () => {
    this.setState(prevState => ({
      popoverOpen: !prevState.popoverOpen
    }));
  };
  closePopover = () => {
    this.setState({
      popoverOpen: false
    });
  };
  onPopoverBlur = () => {
    // you must be asking... WTF? I know... but this timeout is
    // required to make sure we process the onBlur events after the initial
    // event cycle. Reference:
    // https://medium.com/@jessebeach/dealing-with-focus-and-blur-in-a-composite-widget-in-react-90d3c3b49a9b
    window.requestAnimationFrame(() => {
      if (!this.popoverDiv.contains(document.activeElement) && this.props.onBlur) {
        this.props.onBlur();
      }
    });
  };
  registerPopoverDiv = popoverDiv => {
    if (!this.popoverDiv) {
      this.popoverDiv = popoverDiv;
      this.popoverDiv.addEventListener('focusout', this.onPopoverBlur);
    }
  };

  componentWillUnmount() {
    if (this.popoverDiv) {
      this.popoverDiv.removeEventListener('focusout', this.onPopoverBlur);
    }
  }

  onClickItem = onClickAction => {
    this.closePopover();

    if (onClickAction) {
      onClickAction();
    }
  };

  render() {
    const {
      actions,
      itemId,
      item,
      actionEnabled,
      onFocus,
      className
    } = this.props;
    const isOpen = this.state.popoverOpen;
    let allDisabled = true;
    const controls = actions.reduce((controls, action, index) => {
      const key = `action_${itemId}_${index}`;
      const available = action.available ? action.available(item) : true;

      if (!available) {
        return controls;
      }

      const enabled = actionEnabled(action);
      allDisabled = allDisabled && !enabled;

      if (actionIsCustomItemAction(action)) {
        const customAction = action;
        const actionControl = customAction.render(item, enabled);
        const actionControlOnClick = actionControl && actionControl.props && actionControl.props.onClick;
        controls.push(<EuiContextMenuItem key={key} onClick={actionControlOnClick ? actionControlOnClick.bind(null, item) : () => {}}>
              {actionControl}
            </EuiContextMenuItem>);
      } else {
        const {
          onClick,
          name,
          href,
          target,
          'data-test-subj': dataTestSubj
        } = action;
        const buttonIcon = action.icon;
        let icon;

        if (buttonIcon) {
          icon = isString(buttonIcon) ? buttonIcon : buttonIcon(item);
        }

        controls.push(<EuiContextMenuItem key={key} disabled={!enabled} href={href} target={target} icon={icon} data-test-subj={dataTestSubj} onClick={this.onClickItem.bind(null, onClick ? onClick.bind(null, item) : undefined)}>
              {name}
            </EuiContextMenuItem>);
      }

      return controls;
    }, []);
    const popoverButton = <EuiI18n token="euiCollapsedItemActions.allActions" default="All actions">
        {allActions => <EuiButtonIcon className={className} aria-label={allActions} iconType="boxesHorizontal" color="text" isDisabled={allDisabled} onClick={this.togglePopover.bind(this)} onFocus={onFocus} data-test-subj="euiCollapsedItemActionsButton" />}
      </EuiI18n>;
    const withTooltip = !allDisabled && <EuiI18n token="euiCollapsedItemActions.allActions" default="All actions">
        {allActions => <EuiToolTip content={allActions} delay="long">
            {popoverButton}
          </EuiToolTip>}
      </EuiI18n>;
    return <EuiPopover className={className} popoverRef={this.registerPopoverDiv} id={`${itemId}-actions`} isOpen={isOpen} button={withTooltip || popoverButton} closePopover={this.closePopover} panelPaddingSize="none" anchorPosition="leftCenter">
        <EuiContextMenuPanel items={controls} />
      </EuiPopover>;
  }

}
CollapsedItemActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({
    type: PropTypes.oneOfType([PropTypes.oneOf(["button"]), PropTypes.oneOf(["icon"]).isRequired]),
    color: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["primary", "danger", "disabled", "text", "ghost"]).isRequired, PropTypes.func.isRequired]), PropTypes.oneOfType([PropTypes.oneOf(["danger", "disabled", "ghost", "primary", "subdued", "success", "text", "warning"]).isRequired, PropTypes.func.isRequired])]),
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.string,
    available: PropTypes.func,
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool,
    "data-test-subj": PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "annotation", "apmApp", "apmTrace", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAPM", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuLeft", "menuRight", "merge", "metricbeatApp", "metricsApp", "minimize", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "reporter", "reportingApp", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.func.isRequired])
  }).isRequired, PropTypes.shape({
    render: PropTypes.func.isRequired,
    available: PropTypes.func,
    enabled: PropTypes.func,
    isPrimary: PropTypes.bool
  }).isRequired]).isRequired).isRequired,
  item: PropTypes.any.isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]).isRequired,
  actionEnabled: PropTypes.func.isRequired,
  className: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func
};

try {
  // @ts-ignore
  CollapsedItemActions.displayName = "CollapsedItemActions"; // @ts-ignore

  CollapsedItemActions.__docgenInfo = {
    "description": "",
    "displayName": "CollapsedItemActions",
    "props": {
      "actions": {
        "defaultValue": null,
        "description": "",
        "name": "actions",
        "required": true,
        "type": {
          "name": "Action<T>[]"
        }
      },
      "item": {
        "defaultValue": null,
        "description": "",
        "name": "item",
        "required": true,
        "type": {
          "name": "T"
        }
      },
      "itemId": {
        "defaultValue": null,
        "description": "",
        "name": "itemId",
        "required": true,
        "type": {
          "name": "ReactText"
        }
      },
      "actionEnabled": {
        "defaultValue": null,
        "description": "",
        "name": "actionEnabled",
        "required": true,
        "type": {
          "name": "(action: Action<T>) => boolean"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "onFocus": {
        "defaultValue": null,
        "description": "",
        "name": "onFocus",
        "required": false,
        "type": {
          "name": "((event: FocusEvent<Element>) => void) | undefined"
        }
      },
      "onBlur": {
        "defaultValue": null,
        "description": "",
        "name": "onBlur",
        "required": false,
        "type": {
          "name": "(() => void) | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/basic_table/collapsed_item_actions.tsx#CollapsedItemActions"] = {
      docgenInfo: CollapsedItemActions.__docgenInfo,
      name: "CollapsedItemActions",
      path: "src/components/basic_table/collapsed_item_actions.tsx#CollapsedItemActions"
    };
} catch (__react_docgen_typescript_loader_error) {}